// Miroir memoire synchrone du content pack. Le moteur (audio, calculs de rendu,
// utils non-React) lit ces conteneurs de facon SYNCHRONE ; ils sont peuples a
// l'hydratation depuis le content pack (bundle au boot, IndexedDB ensuite).
//
// Reconstruction = inverse exact de l'export (scripts/seed/export-pack.mjs) :
// re-merge la prose de content_texts, re-inverse les renommages snake_case.
// La fidelite base -> pack est prouvee par scripts/seed/verify-pack.mjs (re-export
// deep-equal, hors jest car DB requise) ; la forme par contentStore.lock.test.js.
import contentPack from './content-pack.json';

// ════════════════════════════════════════════════════════════════════════
// Reconstruction pure (rows snake_case + texts -> formes camelCase d'origine)
// ════════════════════════════════════════════════════════════════════════

function indexTexts(textsRows, locale = 'en') {
  const idx = {};
  for (const t of textsRows || []) {
    if (t.deleted || t.locale !== locale) continue;
    (idx[t.entity_type] ??= {});
    (idx[t.entity_type][t.entity_id] ??= {});
    idx[t.entity_type][t.entity_id][t.field] = t.value;
  }
  return idx;
}

function buildModes(rows, tx) {
  const out = {};
  for (const r of rows || []) {
    if (r.deleted) continue;
    const t = tx.mode?.[r.id] || {};
    const m = {
      intervals: r.intervals,
      chordQualities: r.chord_qualities,
      colorDegrees: r.color_degrees,
      scaleType: r.scale_type,
      useFlats: r.use_flats,
      isPentatonic: r.is_pentatonic,
      free: r.free,
      displayName: t.displayName,
      description: t.description,
      characteristicNotes: t.characteristicNotes,
      commonUse: t.commonUse,
      tags: r.tags,
      defaultProgressionKey: r.default_progression_id,
      progressions: r.recommended_progressions,
      aliases: r.aliases,
      relatedModes: r.related_modes,
    };
    if (r.is_natural_minor) m.isNaturalMinor = true; // present seulement sur Aeolian a l'origine
    out[r.id] = m;
  }
  return out;
}

function buildModeToTier(rows) {
  const out = {};
  for (const r of rows || []) {
    if (r.deleted) continue;
    if (r.tier) out[r.id] = r.tier;
  }
  return out;
}

function buildModesByTier(rows) {
  const out = { essentials: [], colors: [], wild: [] };
  for (const r of rows || []) {
    if (r.deleted) continue;
    if (r.tier && out[r.tier]) out[r.tier].push(r.id);
  }
  return out;
}

function buildProgressions(rows, tx) {
  const out = {};
  for (const r of rows || []) {
    if (r.deleted) continue;
    const t = tx.progression?.[r.id] || {};
    out[r.id] = { name: t.name, degrees: r.degrees, description: t.description };
  }
  return out;
}

function buildMoods(rows) {
  return (rows || []).filter((r) => !r.deleted).map((r) => ({ mood: r.id, scales: r.scale_ids }));
}

// Packs de backing tracks : porteurs du prix (l'achat se fait par pack). La prose
// (name/description) vient de content_texts (entity_type 'pack').
function buildPacks(pack, tx) {
  const packs = {};
  for (const r of pack.packs || []) {
    if (r.deleted) continue;
    const t = tx.pack?.[r.id] || {};
    packs[r.id] = {
      id: r.id,
      artistId: r.artist_id,
      name: t.name,
      description: t.description,
      price: r.price,
      bundled: r.bundled === true,
      betaIncluded: r.beta_included === true,
      promoPrice: r.promo_price,
      promoUntil: r.promo_until,
      imageUrl: r.image_url,
      videoUrl: r.video_url,
      position: r.position,
    };
  }
  return packs;
}

// Lecons Guitar Pro (lesson player) : indexees par id, comme les packs. La prose
// (title/description) vient de content_texts (entity_type 'lesson'). `jsonUrl` est ce
// que le lecteur charge a l'ouverture ; le .gp n'est la que pour reconvertir cote Admin.
function buildLessons(pack, tx) {
  const lessons = {};
  for (const r of pack.lessons || []) {
    if (r.deleted) continue;
    const t = tx.lesson?.[r.id] || {};
    lessons[r.id] = {
      id: r.id,
      artistId: r.artist_id,
      title: t.title,
      description: t.description,
      gpUrl: r.gp_url,
      jsonUrl: r.json_url,
      videoUrl: r.video_url,
      thumbnailUrl: r.thumbnail_url,
      rootNote: r.root_note,
      mode: r.mode,
      transposable: r.transposable,
      tempo: r.tempo,
      showScale: r.show_scale,
      stepUnit: r.step_unit,
      tuning: r.tuning,
      tags: r.tags,
      sections: r.sections,
      difficulty: r.difficulty,
      status: r.status,
      packId: r.pack_id,
      free: r.free,
      position: r.position,
      updatedAt: r.updated_at,
    };
  }
  return lessons;
}

function buildArtists(pack, tx) {
  const artists = {};
  for (const r of pack.artists || []) {
    if (r.deleted) continue;
    const t = tx.artist?.[r.id] || {};
    artists[r.id] = {
      id: r.id,
      name: t.name,
      bio: t.bio,
      fullBio: t.fullBio,
      avatar: r.avatar_url,
      productId: r.product_id,
      price: r.price,
      specialties: r.specialties,
      socialLinks: r.social_links,
      visible: r.visible,
      videosByScale: {},
      backingTracksByScale: {},
    };
  }
  for (const v of pack.videos || []) {
    if (v.deleted) continue;
    const a = artists[v.artist_id];
    if (!a) continue;
    const t = tx.video?.[v.id] || {};
    a.videosByScale[v.mode] = {
      id: v.id,
      title: t.title,
      description: t.description,
      duration: v.duration,
      previewDuration: v.preview_duration,
      videoUrl: v.video_url,
      thumbnailUrl: v.thumbnail_url,
    };
  }
  for (const b of pack.backings || []) {
    if (b.deleted) continue;
    const a = artists[b.artist_id];
    if (!a) continue;
    const t = tx.backing?.[b.id] || {};
    // Liste par mode : un artiste peut avoir PLUSIEURS backings dans le meme mode
    // (ex. plusieurs Mixolydian). Une simple affectation par cle ecraserait les
    // doublons ; on accumule donc dans un tableau.
    (a.backingTracksByScale[b.mode] ??= []).push({
      id: b.id,
      title: t.title,
      description: t.description,
      duration: b.duration,
      audioUrl: b.audio_url,
      midiUrl: b.midi_url,
      thumbnailUrl: b.thumbnail_url,
      rootNote: b.root_note,
      mode: b.mode,
      status: b.status,
      updatedAt: b.updated_at, // cache-bust des assets (BackingTrackPlayer) : bouge a chaque edition
      compatibleScales: b.compatible_modes,
      keyCenters: b.key_centers,
      difficulty: b.difficulty,
      packId: b.pack_id,
      free: b.free,
    });
  }
  return artists;
}

// Reconstruit l'ensemble du contenu depuis un pack (rows par categorie + texts).
// Pure : meme entree -> meme sortie. Utilise par le lock deep-equal et l'hydratation.
export function reconstruct(pack, { locale = 'en' } = {}) {
  const tx = indexTexts(pack.texts, locale);
  const MODES = buildModes(pack.modes, tx);
  const CHORD_PROGRESSIONS = buildProgressions(pack.progressions, tx);
  const MOOD_MAPPINGS = buildMoods(pack.moods);
  return {
    MODES,
    MODE_TO_TIER: buildModeToTier(pack.modes),
    MODES_BY_TIER: buildModesByTier(pack.modes),
    CHORD_PROGRESSIONS,
    MOOD_MAPPINGS,
    FEATURED_ARTISTS: buildArtists(pack, tx),
    PACKS: buildPacks(pack, tx),
    LESSONS: buildLessons(pack, tx),
  };
}

// ════════════════════════════════════════════════════════════════════════
// Miroir vivant : conteneurs mutables, peuples EN PLACE (refs stables pour
// les imports). Le moteur lit ces conteneurs de facon synchrone.
// ════════════════════════════════════════════════════════════════════════

export const MODES = {};
export const CHORD_PROGRESSIONS = {};
export const MODE_TO_TIER = {};
export const MODES_BY_TIER = { essentials: [], colors: [], wild: [] };
export const MOOD_MAPPINGS = [];
export const FEATURED_ARTISTS = {};
export const PACKS = {};
export const LESSONS = {};

// Constantes derivees (memes formules que musicTheory/index.js et moodMappings.js).
export const MODE_NAMES_BY_SCALE = {};
export const SCALE_TYPES = [];
export const MODE_NAMES = [];
export const SCALE_TYPES_BY_TIER = { essentials: [], colors: [], wild: [] };
export const MOOD_TO_SCALE = [];
export const ALL_MOODS = [];

const SCALE_TIER_ORDER = ['essentials', 'colors', 'wild'];

// Cles de progressions issues du CONTENU (vs overlay user). Permet de propager les
// suppressions serveur sans toucher aux progressions user superposees par App.js.
let contentProgressionKeys = new Set();

function replaceObject(target, source) {
  for (const k of Object.keys(target)) delete target[k];
  Object.assign(target, source);
}
function replaceArray(target, source) {
  target.length = 0;
  target.push(...source);
}

function recomputeDerived() {
  const modeNamesByScale = {};
  for (const [name, data] of Object.entries(MODES)) {
    (modeNamesByScale[data.scaleType] ??= []).push(name);
  }
  replaceObject(MODE_NAMES_BY_SCALE, modeNamesByScale);
  replaceArray(SCALE_TYPES, [...new Set(Object.values(MODES).map((m) => m.scaleType))]);
  replaceArray(MODE_NAMES, MODE_NAMES_BY_SCALE['Major'] || []);

  const byTier = { essentials: [], colors: [], wild: [] };
  const seen = { essentials: new Set(), colors: new Set(), wild: new Set() };
  for (const tier of SCALE_TIER_ORDER) {
    for (const modeName of MODES_BY_TIER[tier] || []) {
      const scaleType = MODES[modeName]?.scaleType;
      if (scaleType && !seen[tier].has(scaleType)) {
        seen[tier].add(scaleType);
        byTier[tier].push(scaleType);
      }
    }
  }
  replaceObject(SCALE_TYPES_BY_TIER, byTier);

  replaceArray(MOOD_TO_SCALE, MOOD_MAPPINGS.map((m) => ({ mood: m.mood, scale: m.scales[0] })));
  replaceArray(ALL_MOODS, MOOD_MAPPINGS.map((m) => m.mood));
}

// Remplit le miroir en place depuis un pack. Idempotent.
export function hydrateMirror(pack, opts) {
  const r = reconstruct(pack, opts);
  replaceObject(MODES, r.MODES);
  // CHORD_PROGRESSIONS : merge selectif. On retire les cles de CONTENU disparues
  // (suppression serveur propagee) mais on preserve l'overlay des progressions user
  // que App.js superpose sur ce meme objet (jusqu'a la subtask 7).
  for (const k of contentProgressionKeys) {
    if (!(k in r.CHORD_PROGRESSIONS)) delete CHORD_PROGRESSIONS[k];
  }
  Object.assign(CHORD_PROGRESSIONS, r.CHORD_PROGRESSIONS);
  contentProgressionKeys = new Set(Object.keys(r.CHORD_PROGRESSIONS));
  replaceObject(MODE_TO_TIER, r.MODE_TO_TIER);
  replaceObject(MODES_BY_TIER, r.MODES_BY_TIER);
  replaceArray(MOOD_MAPPINGS, r.MOOD_MAPPINGS);
  replaceObject(FEATURED_ARTISTS, r.FEATURED_ARTISTS);
  replaceObject(PACKS, r.PACKS);
  replaceObject(LESSONS, r.LESSONS);
  recomputeDerived();
}

// Baseline synchrone des le chargement du module : le miroir n'est jamais vide,
// l'app fonctionne offline au tout premier lancement (le pack est bundle).
hydrateMirror(contentPack);
